import { ArrowRight, PhoneCall, MapPin, Clock, Sparkles } from "lucide-react";
import { BRAND, STATS } from "@/data/content";

export const Hero = () => {
    return (
        <section
            id="anasayfa"
            data-testid="hero-section"
            className="relative min-h-screen pt-32 pb-20 sm:pt-40 sm:pb-32 overflow-hidden bg-white"
        >
            {/* Background decorations */}
            <div className="absolute inset-0 pointer-events-none">
                <div className="absolute -top-32 -right-32 w-[640px] h-[640px] rounded-full bg-[#0055FF]/8 blur-3xl" />
                <div className="absolute top-1/3 -left-40 w-[420px] h-[420px] rounded-full bg-[#0055FF]/6 blur-3xl" />
                <div className="absolute inset-0 bg-[radial-gradient(rgba(0,85,255,0.05)_1px,transparent_1px)] [background-size:36px_36px] opacity-50" />
            </div>

            <div className="onx-container relative">
                {/* Top badge */}
                <div className="onx-reveal flex items-center gap-2 mb-8">
                    <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full border border-[#0055FF]/20 bg-[#E5EFFF]/60 backdrop-blur-sm">
                        <span className="w-2 h-2 rounded-full bg-[#0055FF] animate-pulse" />
                        <span className="text-[11px] sm:text-xs font-bold uppercase tracking-[0.18em] text-[#0055FF]">
                            Beşiktaş / İstanbul · Şimdi Açık
                        </span>
                    </div>
                </div>

                {/* Main headline */}
                <div className="grid grid-cols-12 gap-6 sm:gap-10 items-start">
                    <div className="col-span-12 lg:col-span-8">
                        <h1
                            data-testid="hero-headline"
                            className="onx-reveal text-[44px] leading-[1.02] sm:text-7xl lg:text-[88px] font-black tracking-[-0.035em] text-[#0B1B2B]"
                            style={{ animationDelay: "100ms" }}
                        >
                            Pırıl pırıl <br />
                            mekânlar,{" "}
                            <span className="relative inline-block">
                                <span className="text-[#0055FF]">
                                    huzurlu nefes.
                                </span>
                                <Sparkles className="absolute -top-4 -right-7 w-8 h-8 text-[#0055FF] animate-float" />
                            </span>
                        </h1>
                        <p
                            data-testid="hero-subtext"
                            className="onx-reveal mt-7 max-w-2xl text-base sm:text-xl leading-relaxed text-[#4A6177]"
                            style={{ animationDelay: "220ms" }}
                        >
                            ONX Care; ev, ofis, kafe ve iş yerleriniz için
                            profesyonel temizlik hizmetleri sunar. Beşiktaş
                            merkezli ekibimiz, tüm İstanbul'a haftanın 7 günü,
                            sabah 07:00 - akşam 21:00 arasında hizmet verir.
                        </p>

                        {/* CTAs */}
                        <div
                            className="onx-reveal mt-9 flex flex-col sm:flex-row gap-3"
                            style={{ animationDelay: "340ms" }}
                        >
                            <a
                                href={BRAND.whatsapp}
                                target="_blank"
                                rel="noopener noreferrer"
                                data-testid="hero-cta-whatsapp"
                                className="onx-btn-primary"
                            >
                                Ücretsiz Teklif Al
                                <ArrowRight className="w-4 h-4" />
                            </a>
                            <a
                                href="#hizmetler"
                                data-testid="hero-cta-services"
                                className="onx-btn-secondary"
                            >
                                Hizmetlerimizi İncele
                            </a>
                        </div>

                        {/* Quick info row */}
                        <div
                            className="onx-reveal mt-12 grid grid-cols-1 sm:grid-cols-3 gap-4 max-w-3xl"
                            style={{ animationDelay: "460ms" }}
                        >
                            <div className="flex items-center gap-3 p-3 rounded-2xl bg-[#F4F8FB]">
                                <div className="w-10 h-10 rounded-xl bg-[#0055FF] flex items-center justify-center shrink-0">
                                    <PhoneCall className="w-4 h-4 text-white" />
                                </div>
                                <div>
                                    <div className="text-[10px] font-bold uppercase tracking-widest text-[#4A6177]">
                                        Bize Ulaşın
                                    </div>
                                    <div className="text-sm font-bold text-[#0B1B2B]">
                                        {BRAND.phoneDisplay}
                                    </div>
                                </div>
                            </div>
                            <div className="flex items-center gap-3 p-3 rounded-2xl bg-[#F4F8FB]">
                                <div className="w-10 h-10 rounded-xl bg-[#0055FF] flex items-center justify-center shrink-0">
                                    <Clock className="w-4 h-4 text-white" />
                                </div>
                                <div>
                                    <div className="text-[10px] font-bold uppercase tracking-widest text-[#4A6177]">
                                        Çalışma Saatleri
                                    </div>
                                    <div className="text-sm font-bold text-[#0B1B2B]">
                                        07:00 – 21:00 · 7/7
                                    </div>
                                </div>
                            </div>
                            <div className="flex items-center gap-3 p-3 rounded-2xl bg-[#F4F8FB]">
                                <div className="w-10 h-10 rounded-xl bg-[#0055FF] flex items-center justify-center shrink-0">
                                    <MapPin className="w-4 h-4 text-white" />
                                </div>
                                <div>
                                    <div className="text-[10px] font-bold uppercase tracking-widest text-[#4A6177]">
                                        Hizmet Bölgesi
                                    </div>
                                    <div className="text-sm font-bold text-[#0B1B2B]">
                                        Tüm İstanbul
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Right side visual */}
                    <div className="col-span-12 lg:col-span-4 relative">
                        <div
                            className="onx-reveal relative aspect-[3/4] rounded-[2rem] overflow-hidden border border-[#0055FF]/10 shadow-[0_30px_80px_-30px_rgba(0,85,255,0.35)]"
                            style={{ animationDelay: "300ms" }}
                        >
                            <img
                                src="https://images.unsplash.com/photo-1625843994098-7a73bad603ce?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NTN8MHwxfHNlYXJjaHwyfHx3YXRlciUyMGRyb3BsZXRzJTIwbWFjcm8lMjBibHVlfGVufDB8fHx8MTc4MDQ5OTY5Nnww&ixlib=rb-4.1.0&q=85"
                                alt="Su damlaları"
                                className="absolute inset-0 w-full h-full object-cover"
                            />
                            <div className="absolute inset-0 bg-gradient-to-t from-[#0055FF]/40 via-transparent to-transparent" />

                            {/* Floating chip */}
                            <div className="absolute top-5 left-5 right-5 flex items-center justify-between">
                                <div className="px-3 py-1.5 rounded-full bg-white/95 backdrop-blur text-[10px] font-bold uppercase tracking-wider text-[#0055FF]">
                                    Hijyen Sertifikalı
                                </div>
                                <div className="w-9 h-9 rounded-full bg-white/95 backdrop-blur flex items-center justify-center">
                                    <Sparkles className="w-4 h-4 text-[#0055FF]" />
                                </div>
                            </div>

                            {/* Bottom info */}
                            <div className="absolute bottom-5 left-5 right-5">
                                <div className="rounded-2xl bg-white/95 backdrop-blur p-4">
                                    <div className="text-xs font-bold uppercase tracking-wider text-[#0055FF] mb-1">
                                        Profesyonel Yaklaşım
                                    </div>
                                    <div className="text-sm font-semibold text-[#0B1B2B] leading-snug">
                                        Endüstriyel ekipman ve sertifikalı
                                        kimyasallarla iz bırakmayan temizlik.
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Stats marquee */}
                <div className="mt-20 sm:mt-28 grid grid-cols-2 sm:grid-cols-4 gap-px bg-[#0055FF]/10 rounded-3xl overflow-hidden border border-[#0055FF]/10">
                    {STATS.map((s) => (
                        <div
                            key={s.label}
                            data-testid={`stat-${s.label}`}
                            className="bg-white p-6 sm:p-8 text-center"
                        >
                            <div className="text-3xl sm:text-5xl font-black tracking-tight text-[#0055FF]">
                                {s.value}
                            </div>
                            <div className="mt-1 text-xs sm:text-sm font-semibold uppercase tracking-[0.15em] text-[#4A6177]">
                                {s.label}
                            </div>
                        </div>
                    ))}
                </div>
            </div>
        </section>
    );
};
